import { WORKER_URL } from '@/lib/convex';

/**
 * Send a chat message via Cloudflare Worker with real SSE streaming.
 * Falls back to local simulation when no API keys are provided.
 *
 * @param {Object} opts
 * @param {Array}  opts.messages        - Full message history [{role, content}]
 * @param {string} opts.model           - Model ID (e.g. 'claude-sonnet-4-6')
 * @param {string} opts.provider        - Provider slug ('anthropic', 'openai', etc.)
 * @param {string} opts.apiKey          - Primary API key
 * @param {string} [opts.fallbackKey]   - Fallback key
 * @param {string} [opts.fallbackProvider] - Fallback provider
 * @param {string} [opts.systemPrompt]  - System prompt to prepend
 * @param {Function} opts.onChunk       - Called with each text chunk string
 * @param {Function} [opts.onFallback]  - Called when fallback key is used
 * @returns {Promise<{content, usedFallback, fallbackProvider}>}
 */
export async function sendChatMessage({
  messages,
  model,
  provider,
  apiKey,
  fallbackKey,
  fallbackProvider,
  systemPrompt,
  userName = 'User',
  memories = [],
  contextFiles = [],
  tools = [],
  brainDoc = null,
  mode = 'api',
  signal,
  onChunk,
  onFallback,
}) {
  // If no API key, run local simulation
  if (!apiKey) {
    return localSimulation({ messages, model, userName, memories, contextFiles, onChunk });
  }

  // Build system prompt from context (includes brain doc for all modes)
  const builtSystemPrompt = buildSystemPrompt({ systemPrompt, memories, contextFiles, userName, tools, brainDoc, mode });

  const response = await fetch(`${WORKER_URL}/api/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    signal,
    body: JSON.stringify({
      messages,
      model,
      provider,
      apiKey,
      fallbackKey,
      fallbackProvider,
      systemPrompt: builtSystemPrompt,
    }),
  });

  if (!response.ok) {
    let errMsg = `${response.status}`;
    try {
      const errData = await response.json();
      errMsg += ': ' + (errData.error || JSON.stringify(errData));
    } catch {
      const errText = await response.text();
      if (errText) errMsg += ': ' + errText;
    }
    throw new Error(errMsg);
  }

  const usedFallback = response.headers.get('X-Used-Fallback') === 'true';
  const usedFallbackProvider = response.headers.get('X-Fallback-Provider') ?? '';
  if (usedFallback && onFallback) onFallback(usedFallbackProvider);

  // Read SSE stream (OpenAI-compatible format)
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let fullContent = '';
  let streamDone = false;

  while (!streamDone) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      const data = line.slice(6).trim();
      if (data === '[DONE]') { streamDone = true; break; }
      try {
        const parsed = JSON.parse(data);
        const chunk = parsed.choices?.[0]?.delta?.content ?? '';
        if (chunk) {
          fullContent += chunk;
          if (onChunk) onChunk(chunk);
        }
      } catch {
        // skip malformed chunks
      }
    }
  }

  reader.cancel().catch(() => {});
  return { content: fullContent, usedFallback, fallbackProvider: usedFallbackProvider };
}

/**
 * Send a message to Cloudflare Workers AI (no user API key needed).
 * CF AI handles inference at the edge; model list: /api/cloudcall-models
 */
export async function sendCloudCallMessage({ messages, model, signal, onChunk }) {
  const response = await fetch(`${WORKER_URL}/api/cloudcall`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    signal,
    body: JSON.stringify({ messages, model }),
  });

  if (!response.ok) {
    let errMsg = `CloudCall ${response.status}`;
    try { const d = await response.json(); errMsg += ': ' + (d.error || JSON.stringify(d)); } catch { /* ignore */ }
    throw new Error(errMsg);
  }

  const modelUsed = response.headers.get('X-Model-Used') || model || '';
  const routingReason = response.headers.get('X-Routing-Reason') || '';

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let fullContent = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';
    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      const raw = line.slice(6).trim();
      if (raw === '[DONE]') continue;
      try {
        const parsed = JSON.parse(raw);
        // Handle multiple CF AI response formats across model families
        const token = parsed.response
          ?? parsed.choices?.[0]?.delta?.content
          ?? parsed.choices?.[0]?.message?.content
          ?? parsed.text
          ?? '';
        if (token) { fullContent += token; onChunk?.(token); }
      } catch { /* skip malformed */ }
    }
  }
  reader.cancel().catch(() => {});
  return { content: fullContent, modelUsed, routingReason };
}

/**
 * Analyze an image using CF Workers AI llava (free, no user key).
 * imageDataUrl can be a full data URI (data:image/png;base64,...) or raw base64.
 * Returns a plain-text description string.
 */
export async function analyzeImageViaCloud(imageDataUrl, userPrompt = '') {
  const response = await fetch(`${WORKER_URL}/api/vision`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      imageBase64: imageDataUrl,
      prompt: userPrompt || 'Describe this image in detail. What do you see?',
    }),
  });
  if (!response.ok) {
    const d = await response.json().catch(() => ({}));
    throw new Error(d.error || `Vision API ${response.status}`);
  }
  const { description, error } = await response.json();
  if (error) throw new Error(error);
  return description || '';
}

/**
 * Generate an image via Cloudflare Workers AI (flux-1-schnell, no user key).
 * Returns a data URL string (image/png).
 */
export async function generateCloudImage(prompt) {
  const response = await fetch(`${WORKER_URL}/api/imagine`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ prompt }),
  });
  if (!response.ok) {
    let errMsg = `Image generation failed (${response.status})`;
    try { const d = await response.json(); errMsg += ': ' + (d.error || JSON.stringify(d)); } catch { /* ignore */ }
    throw new Error(errMsg);
  }
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Builds the system prompt for all modes (api | cloudcall | webgpu).
 * - mode 'api'      : includes tool-call instructions (calendar, memory, artifacts)
 * - mode 'cloudcall': lighter prompt, no tool blocks
 * - mode 'webgpu'   : same as cloudcall; local privacy note added
 */
export function buildSystemPrompt({ systemPrompt, memories, contextFiles, userName, tools, brainDoc = null, mode = 'api' }) {
  const parts = [];
  if (systemPrompt) parts.push(systemPrompt);

  // Inject current date/time (IST) so the AI resolves "today", "tomorrow", etc. correctly
  const now = new Date();
  const istDate = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now); // YYYY-MM-DD
  const istTime = new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: false }).format(now); // HH:MM
  const istDay  = new Intl.DateTimeFormat('en-US', { timeZone: 'Asia/Kolkata', weekday: 'long' }).format(now);

  const modeNote = mode === 'webgpu'
    ? ' You run entirely in the user\'s browser via WebGPU — no data leaves the device.'
    : mode === 'cloudcall'
    ? ' You run on Cloudflare\'s free edge — no API key required.'
    : '';

  parts.push(`You are **Palinos**, a personal AI workspace assistant built by Gnanesh. The user's name is ${userName}.
CRITICAL IDENTITY RULE: Your name is PALINOS. You are NOT Meta AI, LLaMA, ChatGPT, GPT-4, Gemini, Claude, or any other AI. You are Palinos.${modeNote}
Current date: ${istDate} (${istDay}), current time: ${istTime} IST.`);

  // Brain doc — the "infinite memory file". Read before every request.
  if (brainDoc) {
    parts.push('\n## Long-term memory (from past conversations)');
    parts.push(brainDoc);
  }

  if (memories.length > 0) {
    parts.push('\n## Recent session memories');
    memories.slice(-40).forEach((m) => parts.push(`- ${m.content}`));
  }

  if (contextFiles.length > 0) {
    parts.push('\n## User-provided context');
    contextFiles.forEach((f) => {
      if (f.content) parts.push(`### ${f.name}\n${f.content}`);
      else if (f.url) parts.push(`### ${f.name} (link: ${f.url})`);
    });
  }

  if (tools.length > 0) {
    parts.push('\n## Available tools you can invoke');
    tools.filter((t) => t.isActive).forEach((t) => parts.push(`- **${t.name}**: ${t.description}`));
    parts.push('\nWhen you want to use a tool, emit a JSON block: ```tool\n{"tool":"<name>","...params"}\n```');
  }

  // Tool call instructions only for API mode — CloudCall/WebGPU models don't support them
  if (mode !== 'api') {
    return parts.join('\n');
  }

  parts.push(`
## MANDATORY ACTION RULES — follow without exception

### 1. Scheduling — ALWAYS USE TOOL BLOCKS
For EVERY calendar event, task, meeting, or reminder with a date/time — emit a \`\`\`tool\`\`\` JSON block. Never describe events in plain text only.
If the user asks you to add multiple events, emit one tool block per event, back to back.

Today = ${istDate}. Tomorrow = ${(() => { const d = new Date(); d.setDate(d.getDate()+1); return new Intl.DateTimeFormat('en-CA',{timeZone:'Asia/Kolkata'}).format(d); })()}. Use 24h time: "8:30 a.m." → "08:30", "8:30 p.m." → "20:30".

**Add event:**
\`\`\`tool
{"tool":"schedule","title":"<title>","date":"YYYY-MM-DD","time":"HH:MM","description":"<details>"}
\`\`\`

**Remove event** (when user says "remove", "delete", or "cancel" a specific event):
\`\`\`tool
{"tool":"remove_event","title":"<exact event title to remove>"}
\`\`\`

**Update/reschedule event** (when user says "move", "reschedule", or "change time"):
\`\`\`tool
{"tool":"update_event","title":"<exact event title>","date":"YYYY-MM-DD","time":"HH:MM"}
\`\`\`

**Move ALL events from one date to another** (when user says "move everything from X to Y" or "reschedule all of May 3rd to May 2nd"):
\`\`\`tool
{"tool":"move_date_events","fromDate":"YYYY-MM-DD","toDate":"YYYY-MM-DD"}
\`\`\`

ALWAYS emit the correct tool block for add/remove/update/move operations. Do NOT just describe the change in text.

### 2. Proactive memory extraction (CRITICAL)
After EVERY message exchange, silently scan the conversation for facts worth remembering about the user. You MUST emit a memory tool block for each new fact you learn — do not wait to be asked. Extract:
- Full name, nickname, preferred name
- Job, role, company, field of work
- Location, timezone, language preference
- Ongoing projects, goals, deadlines
- Preferences (tools, habits, communication style)
- People they mention (colleagues, friends, family)
- Any personal detail they share

Emit one tool block per distinct fact:
\`\`\`tool
{"tool":"memory","action":"store","content":"<specific fact in one sentence>","category":"name|role|preference|project|person|location|fact"}
\`\`\`

### 3. Artifacts
When you write code or a document, emit:
\`\`\`tool
{"tool":"artifact","title":"<title>","type":"code|document","language":"<lang if code>","content":"<full content>"}
\`\`\`

### Context files
${contextFiles.length > 0 ? `The user has provided ${contextFiles.length} context file(s) above. Read them carefully — use the information to personalise every response, even if the user does not explicitly mention the files.` : ''}

You can include natural language alongside any tool block. Never describe what the user should do — always take the action directly.`);

  return parts.join('\n');
}

function localSimulation({ messages, model, userName, memories, contextFiles, onChunk }) {
  const latestUserMessage = [...messages].reverse().find((m) => m.role === 'user');
  const prompt = latestUserMessage?.content || '';
  const hints = [];
  if (contextFiles.length > 0) hints.push(`${contextFiles.length} context file(s)`);
  if (memories.length > 0) hints.push(`${memories.length} stored memories`);
  const hintText = hints.length > 0 ? ` I have access to your ${hints.join(' and ')}.` : '';

  const content = `Hello ${userName}! You said: "${prompt}"\n\nI'm running in local mode — add your API key in Customise → API to enable real AI responses.${hintText}`;
  if (onChunk) {
    // Simulate streaming by chunking word-by-word
    return new Promise((resolve) => {
      const words = content.split(' ');
      let i = 0;
      const interval = setInterval(() => {
        if (i < words.length) {
          onChunk((i === 0 ? '' : ' ') + words[i]);
          i++;
        } else {
          clearInterval(interval);
          resolve({ content, usedFallback: false, fallbackProvider: '' });
        }
      }, 40);
    });
  }
  return Promise.resolve({ content, usedFallback: false, fallbackProvider: '' });
}

/**
 * Extract memorable facts from a user message using simple patterns.
 * Returns [{content, category}] — used as an automatic fallback when the AI
 * doesn't emit memory tool blocks.
 */
export function extractFactsFromMessage(text) {
  const facts = [];
  const add = (content, category) => facts.push({ content: content.trim(), category });

  // Name
  const nameM = text.match(/(?:my name is|call me|i(?:'m| am) called)\s+([A-Za-z][a-z]+(?:\s[A-Z][a-z]+)?)\b/i)
               || text.match(/(?:i(?:'m| am))\s+([A-Z][a-z]{2,}(?:\s[A-Z][a-z]{2,})?)\b(?!\s+(?:a|an|the|not|just|really|very|so|too))/);
  if (nameM) add(`User's name is ${nameM[1]}`, 'name');

  // Work / company
  const workM = text.match(/(?:i\s+(?:work|worked|am working|was working)\s+(?:at|for|in|with))\s+([^,!?.\n]{3,40})/i)
               || text.match(/(?:i\s+(?:am|was)\s+(?:a|an|the)\s+[^,\n]{3,30}\s+at)\s+([^,!?.\n]{3,40})/i);
  if (workM) add(`Works at ${workM[1].replace(/\s+/g, ' ')}`, 'role');

  // Job title / role
  const roleM = text.match(/i(?:'m| am)\s+(?:a|an)\s+([^,!?.\n]{3,40}?)(?:\s+at|\s+who|\s+and|[,!?.]|$)/i);
  if (roleM && !roleM[1].match(/^(?:sure|ok|happy|glad|here)/i)) add(`Role: ${roleM[1].trim()}`, 'role');

  // Location
  const locM = text.match(/(?:i\s+(?:live|am|'m)\s+(?:in|from|based in|located in))\s+([^,!?.\n]{3,40})/i);
  if (locM) add(`Based in ${locM[1].replace(/\s+/g, ' ')}`, 'location');

  // Projects / building
  const projM = text.match(/(?:i(?:'m| am)\s+(?:building|creating|developing|making|working on))\s+([^,!?.\n]{3,60})/i);
  if (projM) add(`Building: ${projM[1].replace(/\s+/g, ' ')}`, 'project');

  // Company / organisation mentioned as belonging to user
  const compM = text.match(/(?:my\s+(?:company|startup|team|organization|org|project)\s+is|i\s+(?:founded|started|own|run))\s+([^,!?.\n]{3,40})/i);
  if (compM) add(`User's company/project: ${compM[1].replace(/\s+/g, ' ')}`, 'role');

  // Education
  const eduM = text.match(/(?:i\s+(?:studied|graduated|am studying)\s+(?:at|from))\s+([^,!?.\n]{3,40})/i)
              || text.match(/(?:i\s+(?:worked|am)\s+(?:at|in))\s+(iit|nit|iim|[A-Z][a-z]+\s+university|[A-Z][a-z]+\s+college)/i);
  if (eduM) add(`Education/Institution: ${eduM[1].replace(/\s+/g, ' ')}`, 'fact');

  return facts.filter((f) => f.content.length > 5);
}

/**
 * Fetch a URL via the CF Worker crawl endpoint, returns { title, text, wordCount, url } or null.
 */
export async function crawlUrl(url) {
  try {
    const res = await fetch(`${WORKER_URL}/api/crawl?url=${encodeURIComponent(url)}`);
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

/**
 * Parse tool call blocks emitted by the AI in its response text.
 */
export function parseToolCalls(text = '') {
  const result = { scheduleEvents: [], removeEvents: [], updateEvents: [], memoryUpdates: [], artifacts: [], customTools: [] };

  // Primary: structured ```tool blocks
  const regex = /```tool\s*([\s\S]*?)```/g;
  let match;
  while ((match = regex.exec(text)) !== null) {
    try {
      const call = JSON.parse(match[1].trim());
      switch (call.tool) {
        case 'schedule':
          result.scheduleEvents.push({
            title: call.title,
            startTime: new Date(`${call.date}T${call.time || '09:00'}:00+05:30`).getTime(),
            description: call.description,
            allDay: !call.time,
          });
          break;
        case 'remove_event':
          result.removeEvents.push({ title: call.title });
          break;
        case 'update_event':
          result.updateEvents.push({
            title: call.title,
            startTime: call.date ? new Date(`${call.date}T${call.time || '09:00'}:00+05:30`).getTime() : undefined,
          });
          break;
        case 'move_date_events':
          result.moveDateEvents = result.moveDateEvents || [];
          result.moveDateEvents.push({ fromDate: call.fromDate, toDate: call.toDate });
          break;
        case 'memory':
          result.memoryUpdates.push({ action: call.action, content: call.content, category: call.category || 'fact' });
          break;
        case 'artifact':
          result.artifacts.push({ title: call.title, type: call.type, language: call.language, content: call.content });
          break;
        default:
          result.customTools.push(call);
      }
    } catch {
      // skip malformed tool blocks
    }
  }

  // Fallback: detect plain-text calendar entries for models that ignore tool block instructions.
  if (result.scheduleEvents.length === 0) {
    const todayIST = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date());

    const resolveDate = (raw) => {
      const r = (raw || '').trim().toLowerCase().replace(/\.$/, '');
      if (!r || r === 'today') return todayIST;
      if (r === 'tomorrow') {
        const d = new Date(); d.setDate(d.getDate() + 1);
        return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(d);
      }
      if (/^\d{4}-\d{2}-\d{2}$/.test(r)) return r;
      return todayIST;
    };

    const normalizeTime = (raw) => {
      const t = raw.replace(/\./g, '').match(/(\d{1,2})[:\s](\d{2})\s*(am|pm)?/i);
      if (!t) return '09:00';
      let h = parseInt(t[1], 10);
      const m = t[2];
      const mer = (t[3] || '').toLowerCase();
      if (mer === 'pm' && h < 12) h += 12;
      if (mer === 'am' && h === 12) h = 0;
      return `${String(h).padStart(2, '0')}:${m}`;
    };

    const pushEvent = (title, dateRaw, timeRaw) => {
      const t = title.trim().replace(/\*+/g, '').replace(/^[•\-\*\–]\s*/, '');
      if (!t || !timeRaw) return;
      result.scheduleEvents.push({
        title: t,
        startTime: new Date(`${resolveDate(dateRaw)}T${normalizeTime(timeRaw)}:00+05:30`).getTime(),
        description: t,
        allDay: false,
      });
    };

    // Normalize: collapse "•\n" into "• " so bullet+newline becomes single line
    const norm = text.replace(/([•\-\*\–])\s*\n\s*/g, '$1 ');

    // Pattern A: "• Title: Tomorrow at 8:30 a.m." (bullet + title + colon + date + "at" + time)
    const patternA = /[•\-\*\–]\s*(.+?):\s*(today|tomorrow)\s+at\s+(\d{1,2}[:.]\d{2}\s*(?:a\.?m\.?|p\.?m\.?)?)/gi;
    let m2;
    while ((m2 = patternA.exec(norm)) !== null) pushEvent(m2[1], m2[2], m2[3]);

    // Pattern B: "Event/Title: Title\nDate: ...\nTime: ..." block (structured)
    if (result.scheduleEvents.length === 0) {
      const lines = norm.split('\n');
      let lastTitle = '', lastDate = todayIST;
      for (const line of lines) {
        const titleM = line.match(/^(?:\*{0,2})(?:title|event\s*\d*)\s*(?:\d*\s*)?[:\-]\s*(.+)/i);
        if (titleM) { lastTitle = titleM[1].trim().replace(/\*+/g, ''); continue; }
        const dateM = line.match(/^(?:\*{0,2})date[:\-]\s*(.+)/i);
        if (dateM) { lastDate = resolveDate(dateM[1]); continue; }
        const timeM = line.match(/^(?:\*{0,2})time[:\-]\s*(\d{1,2}[:.]\d{2}\s*(?:a\.?m\.?|p\.?m\.?)?)/i);
        if (timeM && lastTitle) { pushEvent(lastTitle, lastDate, timeM[1]); lastTitle = ''; lastDate = todayIST; }
      }
    }

    // Pattern C: any inline "Title: [today|tomorrow] at HH:MM" even without bullet
    if (result.scheduleEvents.length === 0) {
      const patternC = /(?:^|\n)\s*([A-Z][^:\n]{3,80}):\s*(today|tomorrow)\s+at\s+(\d{1,2}[:.]\d{2}\s*(?:a\.?m\.?|p\.?m\.?)?)/gi;
      while ((m2 = patternC.exec(norm)) !== null) pushEvent(m2[1], m2[2], m2[3]);
    }
  }

  return result;
}

/**
 * Fetch the model list from the CF Worker.
 */
export async function fetchModels() {
  try {
    const res = await fetch(`${WORKER_URL}/api/models`);
    if (!res.ok) throw new Error('models fetch failed');
    const models = await res.json();
    return { models };
  } catch {
    // Return local fallback if worker unreachable
    return {
      models: [
        { id: 'claude-sonnet-4-6', name: 'Claude Sonnet 4.6', provider: 'anthropic', apiModel: 'claude-sonnet-4-6', paid: true, free: false, description: 'Balanced power and speed' },
        { id: 'claude-haiku-4-5', name: 'Claude Haiku 4.5', provider: 'anthropic', apiModel: 'claude-haiku-4-5-20251001', paid: true, free: false, description: 'Fastest Claude' },
        { id: 'gpt-4o', name: 'GPT-4o', provider: 'openai', apiModel: 'gpt-4o', paid: true, free: false, description: 'OpenAI flagship' },
        { id: 'llama3-70b-8192', name: 'Llama 3 70B', provider: 'groq', apiModel: 'llama3-70b-8192', paid: false, free: true, description: 'Free via Groq' },
      ],
    };
  }
}

/**
 * Check system health via CF Worker.
 */
export async function checkHealth() {
  try {
    const res = await fetch(`${WORKER_URL}/api/health`);
    if (!res.ok) return { overall: 'degraded', mode: 'worker' };
    const data = await res.json();
    return { overall: 'online', mode: data.mode ?? 'worker' };
  } catch {
    return { overall: 'offline', mode: 'local' };
  }
}

/**
 * OCR: send a PDF file to the CF Worker, get back plain text.
 */
export async function ocrPDF(file) {
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(`${WORKER_URL}/api/ocr`, { method: 'POST', body: formData });
  if (!res.ok) throw new Error(`OCR error ${res.status}`);
  return await res.json();
}
